// src/components/views/IntroView.vue
<script setup>
import { computed } from 'vue'
import { useAppStore } from '@/stores/app'
import { useDataStore } from '@/stores/data'
import BasePage from './BasePage.vue'
import DefaultHeader from '@/components/shared/DefaultHeader.vue'
import LazyLottie from '@/components/shared/LazyLottie.vue'
import animationDataJson from '@/assets/animations/location_pin_kogenta.json'
import { ElMessageBox } from 'element-plus'

const appStore = useAppStore()
const dataStore = useDataStore()

const active = () => {
	setTimeout(() => {
		appStore.bottomSheet.setHeightNormal(800, 'IntroPage: active', true)
	}, 300)
}

const reload = () => {
	document.location.reload()
}

const showTrips = () => {
	appStore.setPage('trips')
}

const addJourney = () => {
	dataStore.createEmptyJourneyDay()
	appStore.setPage('edit')
}

const confirmNoTrips = () => {
	ElMessageBox.confirm('Er du sikker på at du ikke foretok noen reiser denne dagen?', 'Bekreft ingen reiser', { confirmButtonText: 'Ja', cancelButtonText: 'Nei' }).then(() => {
		//dataStore.confirmNoTrips()
		dataStore.createEmptyJourneyDay(true)
		appStore.setPage('edit')
		//appStore.setPage('approved')
		appStore.editStep = ''
	})
}

const openReadMore = () => {
	kettle.openUrl('https://www.opinion.no/rvu')
}

const hasDatePicker = computed(() => {
	if (dataStore.userCanChangeDates) {
		return true
	}
	if (((dataStore.hasData && dataStore.currentJourney?.trips?.length) || dataStore.selectedDate) && dataStore.availableDates && Object.keys(dataStore.availableDates).length > 1) {
		return true
	}
	return false
})

const title = computed(() => {
	if (dataStore.hasData && dataStore.currentJourney) {
		// The Date picker will present the selected date
		if (Object.keys(dataStore.availableDates).length > 1 || dataStore.userCanChangeDates) {
			return 'Reisedagbok'
		} else {
			var date = new Date(dataStore.currentJourney.date)
			var dateFormatted = date.toLocaleDateString('nb-NO', { month: 'long', day: 'numeric' })
			return 'Reisedagbok for ' + dateFormatted
		}
	}
	if (!dataStore.hasData && dataStore.selectedDate && !dataStore.justInstalled) {
		return 'Reisedagbok'
	}
	return 'Reiseanalyse pågår'
})
</script>

<template>
	<BasePage name="intro" @active="active()" :title="title">
		<template #header>
			<DefaultHeader :title="title" :hasDatePicker="hasDatePicker" :hasRefreshButton="!hasDatePicker" />
		</template>

		<template v-if="dataStore.hasDayReady && dataStore.hasData">
			<p>
				<strong>
					Reisedagbok for {{ dataStore.selectedDateFormatted_WWWddMMM }} er nå klar!<br />
					Vi registrerer og forsøker å forstå dine reiser automatisk, men vi trenger din hjelp for å få alt helt riktig.
				</strong>
			</p>
			<p>Gå gjennom reisedagboken din for å:</p>
			<ul class="list-disc pl-4">
				<li>Sjekke at reisene og etappene stemmer.</li>
				<li>Gjøre nødvendige rettelser hvis noe mangler eller er feil.</li>
				<li>Svare på et spørreskjema for detaljer om reisene.</li>
			</ul>
			<p>Store og små turer som henger sammen med <strong>samme formål</strong> regner vi som én reise. Dette kan for eksempel være:</p>
			<ul class="list-disc pl-4 mt-2">
				<li>En reise til jobb med flere etapper og transportmidler.</li>
				<li>En handletur med bilen.</li>
				<li>En luftetur med hunden.</li>
			</ul>
			<p>Din tilbakemelding er viktig og hjelper oss med å forstå reisevanene i Norge enda bedre.</p>
			<p><strong>Tusen takk for hjelpen!</strong></p>
		</template>

		<template v-if="!dataStore.hasDayReady || !dataStore.hasData">
			<template v-if="!dataStore.selectedDate || dataStore.justInstalled || !dataStore.hasDayReady">
				<h2>Takk for at du bruker Reisevaner!</h2>

				<div class="flex flex-row items-center justify-center">
					<div>
						<p>Prosessen med å lage din reisedagbok er i gang! Vi samler inn data om dine bevegelser for å lage en oversikt over reisene dine.</p>
						<p v-if="dataStore.yesterDaySelected"><strong>Vi ønsker at du svarer for gårsdagen, og den blir tilgjengelig kl 12:00.</strong></p>
					</div>

					<!-- Lazy-loaded Lottie Component -->
					<LazyLottie :animationData="animationDataJson" :height="100" :loop="true" :autoplay="true">
						<template #loading>
							<div class="w-[100px] h-[100px] flex items-center justify-center text-4xl">📍</div>
						</template>
					</LazyLottie>
				</div>

				<h2 class="mt-6">Hva skjer nå?</h2>
				<ul class="list-disc pl-4 space-y-2">
					<li>Appen samler inn data om dine bevegelser, reiser og offentlig transport.</li>
					<li>Du trenger ikke gjøre noe aktivt – bare ha telefonen med deg når du reiser.</li>
					<li>Du vil få et varsel når vi har nok data, og du kan gå gjennom reisedagboken din.</li>
					<li>Du går gjennom reisene, gjør justeringer ved behov og svarer på noen oppfølgende spørsmål.</li>
				</ul>
			</template>
			<template v-if="dataStore.hasDayReady">
				<h2>Vi har ikke registrert noen reiser for {{ dataStore.selectedDateFormatted_WWWddMMM }}.</h2>
				<template v-if="dataStore.userCanChangeDates && dataStore.hasAvailableDates">
					<p>Du kan trykke på datoen over for å åpne kalenderen og velge en annen dag som har registrerte bevegelser.</p>
				</template>
				<p>Dersom du ikke foretok noen reiser denne dagen, kan du trykke på knappen under for å bekrefte dette, ettersom dette også er nyttig informasjon når det gjelder reisevaner.</p>
				<p>Dersom du faktisk hadde en eller flere reiser denne dagen, kan det hende appen ikke har klart å fange opp dette korrekt. Velg da å legge til manglende reiser manuelt.</p>
			</template>
		</template>

		<template #footer v-if="dataStore.hasDayReady">
			<template v-if="!dataStore.hasData">
				<div class="button full orange" @click="addJourney()">
					Legg til reise
					<i class="fa-solid fa-plus"></i>
				</div>
				<div class="button full green" @click="confirmNoTrips()">
					Jeg reiste ikke
					<i class="fa-solid fa-hourglass-half"></i>
				</div>
			</template>
			<template v-else>
				<div class="button" @click="openReadMore()">
					<i class="fa-solid fa-up-right-from-square"></i>
					Les mer
				</div>
				<div class="button blue full" @click="showTrips()">
					Gå igjennom reiser
					<i class="fa-regular fa-map"></i>
				</div>
			</template>
		</template>
	</BasePage>
</template>
